import type {
  AssignmentResult,
  AssignmentRule,
  CompareExpression,
  RuleExpression,
} from '@tsagko/label-contracts'

/**
 * Pure rule evaluator for AssignmentRule[] against a data record.
 *
 * - Filters disabled rules (`enabled === false`); `undefined` or `true` → included.
 * - Evaluates each rule's nested `RuleExpression` tree (compare / and / or / not).
 * - Returns matched rules sorted by `priority` descending (stable; undefined → 0).
 * - Each matched entry contains `ruleId` plus the rule's `assignments` fields.
 *
 * Pure: never mutates inputs, never throws on bad regex / non-array `in` value.
 */
export function evaluateRules(
  rules: AssignmentRule[],
  data: Record<string, unknown>
): AssignmentResult {
  const enabledRules = rules.filter((r) => r.enabled !== false)

  // Track original index for stable sort.
  const matches: Array<{
    rule: AssignmentRule
    index: number
  }> = []
  enabledRules.forEach((rule, index) => {
    if (evaluateExpression(rule.condition, data)) {
      matches.push({ rule, index })
    }
  })

  matches.sort((a, b) => {
    const pa = a.rule.priority ?? 0
    const pb = b.rule.priority ?? 0
    if (pb !== pa) return pb - pa
    return a.index - b.index
  })

  return {
    matched: matches.map(({ rule }) => {
      const entry: AssignmentResult['matched'][number] = { ruleId: rule.id }
      const { templateId, printerProfileId, propOverrides, tags } =
        rule.assignments
      if (templateId !== undefined) entry.templateId = templateId
      if (printerProfileId !== undefined) entry.printerProfileId = printerProfileId
      if (propOverrides !== undefined) entry.propOverrides = propOverrides
      if (tags !== undefined) entry.tags = tags
      return entry
    }),
  }
}

function evaluateExpression(
  expr: RuleExpression,
  data: Record<string, unknown>
): boolean {
  switch (expr.op) {
    case 'compare':
      return evaluateCompare(expr, data)
    case 'and':
      return expr.operands.every((o) => evaluateExpression(o, data))
    case 'or':
      return expr.operands.some((o) => evaluateExpression(o, data))
    case 'not':
      return !evaluateExpression(expr.operand, data)
  }
}

function evaluateCompare(
  expr: CompareExpression,
  data: Record<string, unknown>
): boolean {
  const { field, operator, value } = expr
  const fieldValue = data[field]

  switch (operator) {
    case '=':
      return fieldValue === value
    case '!=':
      return fieldValue !== value
    case '<': {
      const a = Number(fieldValue)
      const b = Number(value)
      return a < b
    }
    case '<=': {
      const a = Number(fieldValue)
      const b = Number(value)
      return a <= b
    }
    case '>': {
      const a = Number(fieldValue)
      const b = Number(value)
      return a > b
    }
    case '>=': {
      const a = Number(fieldValue)
      const b = Number(value)
      return a >= b
    }
    case 'contains':
      return String(fieldValue).includes(String(value))
    case 'startsWith':
      return String(fieldValue).startsWith(String(value))
    case 'endsWith':
      return String(fieldValue).endsWith(String(value))
    case 'in':
      if (!Array.isArray(value)) return false
      return value.includes(fieldValue)
    case 'exists':
      return (
        field in data && data[field] !== null && data[field] !== undefined
      )
    case 'matches': {
      let pattern: RegExp
      try {
        pattern = new RegExp(String(value))
      } catch {
        return false
      }
      return pattern.test(String(fieldValue))
    }
  }
}
